import {useCallback, useEffect, useRef, useState} from 'react';
import {UserService} from '../services';

const POLL_INTERVAL_MS = 2500;
const POLL_MAX_ATTEMPTS = 24;
const REDIRECT_TTL_MS = 15 * 60 * 1000;

// Ключ ідемпотентності: потрібен для ретраю setup-sessions, живе до успішної прив'язки
const idemKeyOf = (eventId: number) => `card-setup-idempotency-key:${eventId}`;
// Позначка "користувача відправили на Stripe / бекенд обробляє": потрібна лише для polling при вході
const redirectedKeyOf = (eventId: number) => `card-setup-redirected-at:${eventId}`;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export type WaitResult = 'attached' | 'timeout' | 'stopped';
export type StartResult = 'redirect' | 'attached' | 'processing';

export const clearCardSetupRedirect = (eventId: number) => sessionStorage.removeItem(redirectedKeyOf(eventId));

const markCardSetupRedirect = (eventId: number) => sessionStorage.setItem(redirectedKeyOf(eventId), String(Date.now()));

export const hasPendingCardSetup = (eventId: number): boolean => {
  const raw = sessionStorage.getItem(redirectedKeyOf(eventId));
  if (!raw) return false;

  if (Date.now() - Number(raw) > REDIRECT_TTL_MS) {
    clearCardSetupRedirect(eventId);
    return false;
  }
  return true;
};

export const useCardAttachment = (eventId: number | undefined, returnRoute: string) => {
  const [hasSavedCard, setHasSavedCard] = useState<boolean | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const runIdRef = useRef(0);

  // зупиняємо polling при розмонтуванні
  useEffect(
    () => () => {
      runIdRef.current += 1;
    },
    []
  );

  const refresh = useCallback(async (): Promise<boolean> => {
    if (!eventId) return false;

    const {data} = await UserService.ensureUserHaveAttendeeIdentity(eventId);

    if (data.hasSavedPaymentMethod) {
      // прив'язка пройшла: ключ і позначка більше не потрібні
      sessionStorage.removeItem(idemKeyOf(eventId));
      clearCardSetupRedirect(eventId);
    }

    setHasSavedCard(data.hasSavedPaymentMethod);
    return data.hasSavedPaymentMethod;
  }, [eventId]);

  const stopWaiting = useCallback(() => {
    runIdRef.current += 1;
    setIsWaiting(false);
  }, []);

  const waitForAttachment = useCallback(async (): Promise<WaitResult> => {
    // eslint-disable-next-line no-plusplus
    const runId = ++runIdRef.current;
    setIsWaiting(true);

    try {
      for (let i = 0; i < POLL_MAX_ATTEMPTS; i += 1) {
        // polling має бути послідовним: наступна спроба залежить від попередньої
        /* eslint-disable no-await-in-loop */
        if (await refresh()) return 'attached';
        if (runId !== runIdRef.current) return 'stopped';
        await sleep(POLL_INTERVAL_MS);
        /* eslint-enable no-await-in-loop */
        if (runId !== runIdRef.current) return 'stopped';
      }

      // timeout: знімаємо позначку, щоб наступний візит не запускав polling.
      // Ключ ідемпотентності лишається для ретраю.
      if (eventId) clearCardSetupRedirect(eventId);
      return 'timeout';
    } finally {
      if (runId === runIdRef.current) setIsWaiting(false);
    }
  }, [eventId, refresh]);

  const startAttachment = useCallback(async (): Promise<StartResult> => {
    if (!eventId) throw new Error('Event is not loaded');

    setIsStarting(true);
    try {
      const idemKey = idemKeyOf(eventId);
      let key = sessionStorage.getItem(idemKey);
      if (!key) {
        key = crypto.randomUUID();
        sessionStorage.setItem(idemKey, key);
      }

      const {data} = await UserService.startUserCardAttachment(key, returnRoute);

      switch (data.status as string) {
        case 'pending':
          markCardSetupRedirect(eventId);
          window.location.assign(data.setupUrl);
          return 'redirect';
        case 'already_attached':
          await refresh();
          return 'attached';
        case 'processing':
          markCardSetupRedirect(eventId);
          return 'processing';
        default:
          throw new Error('Unknown card setup status');
      }
    } finally {
      setIsStarting(false);
    }
  }, [eventId, refresh]);

  return {hasSavedCard, isStarting, isWaiting, refresh, stopWaiting, waitForAttachment, startAttachment};
};
